import { FC } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Location, useLocation, useParams } from 'react-router-dom'
import { FormGenerator, FormInputProps } from '@microservices/wiskey-react-components'

import { useViewTitleDuplicateValidation } from '@hooks'
import { ENTITY, GENERATOR_INPUT_TYPE, REGEX } from '@constants'

type NewModelProps = {
  loading?: boolean
  isCodeDisabled?: boolean
  defaultTitle?: string
  entityType?: ENTITY
}

export type NewModelView = {
  code: string
  title: string
}

export type NewModelForm = {
  code: string
  title: string
}

export const NewModel: FC<NewModelProps> = ({
  loading,
  isCodeDisabled,
  defaultTitle: defaultTitleProp,
  entityType,
}) => {
  const { t } = useTranslation()
  const { code, id } = useParams()
  const location = useLocation() as Location & { state?: { code?: string } }
  const copyCode = location.state?.code

  const { formState } = useFormContext<{ title?: string }>()
  const defaultTitle =
    defaultTitleProp ?? (formState.defaultValues?.title as string | undefined) ?? ''

  const mode = code || id ? 'edit' : copyCode ? 'copy' : 'create'
  const viewTitleValidation = useViewTitleDuplicateValidation({ defaultValue: defaultTitle })

  const validateInputString = (value: string) => {
    if (value.trim().length === 0) return t('error.onlyWhitespacesTitleInput')
    if (!REGEX.CHECK_LETTERS_DASH_UNDERLINE_WHITESPACE_NUMBERS.test(value))
      return t('error.notValidSymbolsTitleInput')

    if (entityType === ENTITY.VIEW) {
      return viewTitleValidation.validate(value, mode)
    }
  }

  const inputsConfig: FormInputProps[] = [
    {
      name: 'code',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: t('viewsCreate.newModel.code.placeholder'),
      label: t('viewsCreate.newModel.code.label'),
      disabled: !!(code || id) || isCodeDisabled,
      replacePattern: REGEX.MODEL_CODE_REPLACE_PATTERN,
      rules: {
        required: true,
        validate: value => value.trim().length !== 0,
      },
      formInputContainerSx: { mb: 0.5 },
    },
    {
      formInputContainerSx: {
        mb: 0,
      },
      name: 'title',
      inputType: GENERATOR_INPUT_TYPE.INPUT,
      placeholder: t('viewsCreate.newModel.title.placeholder'),
      label: t('viewsCreate.newModel.title.label'),
      rules: { required: true, validate: validateInputString },
    },
  ]

  return <FormGenerator inputs={inputsConfig} loading={loading} mainGridSpacing={0} />
}
